import React, { Fragment, useContext } from "react"
import { Alert } from "react-bootstrap"
import { Skeleton } from '@mui/material';

import Indent from "./Indent"
import { Spaces } from "./Spaces"
import { FormattedNumber } from "./FormattedNumber"
import SkeletonText from "./SkeletonText"
import { TextDisplayToken } from "./TextDisplay/TextDisplay"
import { TextCopyQR } from "./WithPopup/TextCopyQR"
import useLog from "../hooks/useLog"
import { UserContext } from "../Contexts/UserContext/UserContext"
import { toFixedDown } from "../helpers/convert_bsv_tokens"
import { AccountBalanceWallet } from '@mui/icons-material';
import Constants, { appConfig } from "../config.js"


/**
 * props:
 * 	 - noicon		remove the wallet icon
 * 	 - row			compact single-row list item (no wallet icon, no "balance" label,
 * 	  				keeps token gradient background + logo watermark) — used to list all tokens
 *   - address		optional string to display "receive address", or null to not display receive address
 */
export default function SoldeBSV(props) {
	const function_id = "SoldeBSV: "
	const log = useLog(function_id)

	const userContext = useContext(UserContext)

	const coinProps = userContext.get_coin_props("bsv")
	const accent = (coinProps && coinProps.color) || "#e8b21f"  // BSV gold

	var jsx_soldebsv_loader = <SkeletonText width="6em" height="1.9em" />

	const balancebsv = userContext.balance_bsv
	const bsvprice = userContext.bsvprice
	// Mock ("example") wallet: balance is hydrated to 0 explicitly and bsvprice
	// may never land (no real fetch path), so treat a set balance as "ready"
	// even without a price. EUR is 0 anyway (0 BSV × any price = 0 €).
	const isMock = userContext.isMockWallet === true

	log("balancebsv: ", balancebsv,
		"bsvprice: ", bsvprice, "isMock:", isMock)

	// ---- balance block ---------------------------------------------------
	var jsx_soldebsv = null
	if (balancebsv != null && (bsvprice || isMock)) {
		var soldeeur = bsvprice
			? toFixedDown(parseFloat(balancebsv / 1e8 * bsvprice), 2)
			: 0  // mock wallet: no price yet, but balance is 0 → EUR is 0

		var jsx_euro = <Fragment>
			<FormattedNumber value={soldeeur} decimals="2" separator=" " /> €
		</Fragment>

		var jsx_bsv = <Fragment>
			<FormattedNumber value={balancebsv * 1e-8} decimals="8" separator=" " /> BSV
		</Fragment>

		var jsx_satoshis = <Fragment>
			<FormattedNumber value={balancebsv} decimals="0" separator=" " /> satoshis
		</Fragment>

			jsx_soldebsv = (
				<div style={{ textAlign: "center", margin: "0.35em 0 0.2em" }}>
					<div style={{
						fontSize: "1.9em",
						fontWeight: 600,
						letterSpacing: "0.5px",
						color: accent,
						textShadow: "0 1px 3px rgba(0,0,0,0.4)",
					}}>
						{jsx_euro}
					</div>
					{props.condensed ? null : (
						<div style={{
							marginTop: "0.4em",
							color: appConfig.theme.textColor2,
							fontSize: "0.9em",
							display: "flex",
							justifyContent: "center",
							gap: "1em",
						}}>
							<span>{jsx_bsv}</span>
							<span style={{ opacity: 0.7 }}>({jsx_satoshis})</span>
						</div>
					)}
				</div>
			)
		
	}

	// ---- header label -----------------------------------------------------
	var jsx_icon = props.noicon ? null : (
		<Fragment>
			<AccountBalanceWallet />
		</Fragment>
	)

	const jsx_header = (
		<div style={{
			display: "flex",
			alignItems: "center",
			justifyContent: "center",
			gap: "0.5em",
			color: appConfig.theme.textColor2,
			fontSize: "0.95em",
			letterSpacing: "0.5px",
			textTransform: "uppercase",
		}}>
			{jsx_icon}
			<span>BSV balance</span>
		</div>
	)

	// ---- receive address card --------------------------------------------
	var jsx_address = null
	if (props.address) {
		jsx_address = (
			<div style={{
				marginTop: "1.4em",
				backgroundColor: "rgba(0,0,0,0.22)",
				border: "1px solid rgba(255,255,255,0.06)",
				borderRadius: "0.7em",
				padding: "1.1em 1.2em",
			}}>
				<div style={{
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
					gap: "0.55em",
					color: "white",
					fontWeight: 600,
					fontSize: "1em",
					marginBottom: "0.85em",
					letterSpacing: "0.3px",
				}}>
					<i className="fas fa-hand-holding-usd" />
					<span>Receive</span>
				</div>

				{userContext.isMockWallet ?
					<Alert variant="light" style={{ margin: "0", padding: "0.55em", fontSize: "15px" }}>
						<i style={{ fontSize: "25px", marginRight: "10px" }} className="fa fa-info-circle" />
						{Constants.MOCK_WALLET_PHRASE}
					</Alert>
					:
					<>
						<div style={{
							backgroundColor: "rgba(255,255,255,0.04)",
							borderRadius: "0.5em",
							padding: "0.8em 0.9em",
							fontFamily: "monospace",
							fontSize: "14px",
							wordBreak: "break-all",
							textAlign: "center",
						}}>
							<TextCopyQR value={props.address} />
						</div>
						<br />
						<Alert variant="light" style={{ margin: "0", padding: "0.55em", fontSize: "15px" }}>
							<i style={{ fontSize: "25px", marginRight: "10px" }} className="fa fa-info-circle" />
							The "bsv:" prefix improves the security of your transactions on this app.
							Remove it when using another app.
							If another app offers multiple sending networks, always choose the BSV network.
						</Alert>
					</>
				}
			</div>
		)
	}

	// ---- row render (compact list item) ----------------------------------
	// slim single-row entry for listing tokens: no wallet icon, no "balance"
	// label, but keeps the token gradient background + right-side logo watermark.
	if (props.row) {
		const has_balance = balancebsv != null && (bsvprice || isMock)
		var soldeeur_row = has_balance
			? (bsvprice ? toFixedDown(parseFloat(balancebsv / 1e8 * bsvprice), 2) : 0)
			: null

		return (
			<div style={{
				position: "relative",
				zIndex: 0,
				overflow: "hidden",
				background: (coinProps && coinProps.background_css) || "",
				borderRadius: "0.5em",
				padding: "1.5em 1.2em",
				color: appConfig.theme.textColor1,
			}}>
				{coinProps && coinProps.logo_banner ?
					<img src={coinProps.logo_banner} alt={coinProps.ticker + " logo"}
						style={{
							top: "50%",
							right: "0%",
							transform: "translateY(-50%)",
							opacity: 0.13,
							zIndex: -1,
							overflow: "hidden",
							position: "absolute",
							borderRadius: "12px",
							height: "180%",
						}} />
					: null}

				<div style={{
					display: "flex",
					alignItems: "center",
					justifyContent: "space-between",
					gap: "0.8em",
					position: "relative",
					zIndex: 1,
				}}>
					<div style={{
						display: "flex", alignItems: "center",
						gap: "0.7em", minWidth: 0,
					}}>
						{coinProps && coinProps.logo ?
							<img src={coinProps.logo} alt={coinProps.ticker + " logo"}
								style={{
									height: "40px", width: "40px",
									objectFit: "contain", flexShrink: 0,
								}} />
							: null}
						<span style={{
							fontWeight: 700,
							fontSize: "1.25em",
							whiteSpace: "nowrap",
							overflow: "hidden",
							textOverflow: "ellipsis",
						}}>
							{coinProps ? coinProps.name : "Bitcoin SV"}
						</span>
					</div>

					<div style={{
						display: "flex",
						alignItems: "baseline",
						gap: "0.4em",
						color: accent,
						fontWeight: 700,
						whiteSpace: "nowrap",
						textShadow: "0 1px 3px rgba(0,0,0,0.4)",
					}}>
						<span style={{ fontSize: "1.8em", letterSpacing: "0.5px", lineHeight: 1 }}>
							{has_balance ?
								<FormattedNumber value={soldeeur_row} decimals="2" separator=" " />
								: <Skeleton variant="text"
									width="2.5em"
									height="1em"
									sx={{
										display: "inline-block",
										bgcolor: "rgba(255,255,255,0.12)",
									}} />}
						</span>
						<span style={{
							fontSize: "1em",
							fontWeight: 700,
							letterSpacing: "1px",
							opacity: 0.85,
							minWidth: "2.5em",
							textAlign: "left",
						}}>
							€
						</span>
					</div>
				</div>
			</div>
		)
	}

	return (
		<TextDisplayToken tokenid="bsv">
			<div style={{ padding: "0.2em 0" }}>
				{jsx_header}
				{jsx_soldebsv || jsx_soldebsv_loader}
				{jsx_address}
			</div>
		</TextDisplayToken>
	)
}




// // on this one you click on the one you want to put as first, allowing any order
// function SoldeBSV_clickToReorder (props) {
// 	/**
// 	 * props:
// 	 * 	 - noicon		remove wallet icon
// 	 * 	 - condensed	display as a condensed version
// 	 * 	 - legacy		old version as a single line (€, bsv & satoshis at same fontsize)
// 	*/
// 	const context = useContext(UserContext)
// 	const [displayOrder, set_displayOrder] = useState(["euro", "bsv", "satoshis"])

// 	var jsx_soldebsv_loader	= <Loader />

// 	const balancebsv = context.balance_bsv
// 	const bsvprice = context.bsvprice

// 	const switch_display = (new_first) => {
// 		var new_displayOrder = displayOrder

// 		var index = new_displayOrder.indexOf(new_first)
// 		if (index !== -1) new_displayOrder.splice(index, 1)

// 		new_displayOrder = [new_first, ...new_displayOrder]

// 		console.log("new_displayOrder=",new_displayOrder)
// 		set_displayOrder(new_displayOrder)
// 	}
	
// 	var jsx_soldebsv = null
// 	if(bsvprice && balancebsv) {
// 		var soldeeur = toFixedDown(parseFloat(balancebsv / 1e8 * bsvprice), 2)

// 		var jsx_euro, jsx_bsv, jsx_satoshis

// 		jsx_euro =<span style={{cursor:"pointer"}} onClick={()=>switch_display("euro")} >
// 				<FormattedNumber value={soldeeur} decimals="2" separator=" "/> €
// 			</span>

// 		jsx_bsv = <span style={{cursor:"pointer"}} onClick={()=>switch_display("bsv")} >
// 				<FormattedNumber value={balancebsv*1e-8} decimals="8" separator=" " /> BSV 
// 			</span>

// 		jsx_satoshis = <span style={{cursor:"pointer"}} onClick={()=>switch_display("satoshis")} >
// 				<FormattedNumber value={balancebsv} decimals="0" separator=" " /> satoshis 
// 			</span>

// 		function get_correct_jsx(name){
// 			if(name.includes("euro")) return jsx_euro
// 			if(name.includes("bsv")) return jsx_bsv
// 			if(name.includes("satoshis")) return jsx_satoshis
// 		}

// 		var jsx_first, jsx_second, jsx_third
// 		jsx_first = get_correct_jsx(displayOrder[0])
// 		jsx_second = get_correct_jsx(displayOrder[1])
// 		jsx_third = get_correct_jsx(displayOrder[2])

// 		// 1e8 = 10^8, beware 10e8 != 10^8
// 		jsx_soldebsv = 
// 			<Fragment>
// 				<Indent/>{jsx_first}
// 				<Indent/> ( {jsx_second} )
// 				<Indent/> ( {jsx_third} )
// 			</Fragment>

// 		if(!props.legacy){
// 			jsx_soldebsv = 
// 				<Fragment>
// 					<div style={{fontSize: "1.7em", fontWeight:"bold", marginLeft:"1.8em"}}>
// 						{jsx_first} 
// 					</div>
// 					<div style={{fontStyle: "italic"}}>
// 					<Indent/>{jsx_second}
// 					<Indent/>({jsx_third})
// 					{/* <Indent/><i style={{cursor:"pointer"}} onClick={switch_display} className="fas fa-retweet" /> */}
// 					<br/>
// 					</div>
// 				</Fragment>
// 		}
// 	}

// 	var jsx_icon = 
// 		<Fragment>
// 			<i className="fas fa-wallet" />
// 		</Fragment>

// 	if(props.noicon) jsx_icon = null

// 	if(props.condensed){
// 		return(
// 			<Fragment>
// 				<TextDisplay>
// 				{jsx_icon} Solde BSV {jsx_soldebsv || jsx_soldebsv_loader}
// 				</TextDisplay>
// 			</Fragment>
// 		)
// 	}

// 	return (
// 		<Fragment>
// 			{/* context: {JSON.stringify(context)}<br/> */}

// 			<h1>
// 				{jsx_icon?
// 					<Fragment>
// 						{jsx_icon}
// 						<Spaces count="3" />
// 					</Fragment>
// 				:null}
// 				Solde BSV
// 			</h1><br/>
			
// 			<TextDisplay>
// 				{jsx_soldebsv || jsx_soldebsv_loader}
// 			</TextDisplay>

// 		</Fragment>
// 	)
// }
